import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../lib/apiClient';

export function useDriverOrders({ loggedIn, token, driver, audioEnabled, setLocation, onError }) {
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const lastOrderCountRef = useRef(0);

  const _clearOrdersState = useCallback(() => {
    setOrders([]);
    setCurrentOrder(null);
    lastOrderCountRef.current = 0;
    setOrdersError(null);
    setLoadingOrders(false);
  }, []);

  useEffect(() => {
    const onDriverLogout = () => {
      _clearOrdersState();
    };
    window.addEventListener('driver_logout', onDriverLogout);
    return () => window.removeEventListener('driver_logout', onDriverLogout);
  }, [_clearOrdersState]);

  const fetchOrders = useCallback(async (driverId = null, opts = {}) => {
    const silent = opts.silent === true;
    const id = driverId ?? driver?.id;
    if (!id) return;

    if (!silent) {
      setLoadingOrders(true);
      setOrdersError(null);
    }
    try {
      const response = await apiClient.get(`/drivers/${id}/orders/`);
      const orderData = response.data || [];
      setOrders(orderData);
      const activeOrder = orderData.find((o) => o.status === 'in_route' || o.status === 'ready');
      setCurrentOrder(activeOrder || null);

      if (orderData.length > lastOrderCountRef.current && audioEnabled) {
        new Audio('/notification.mp3').play().catch(() => {});
      }
      lastOrderCountRef.current = orderData.length;

      if (orderData.length > 0) {
        console.log(`✅ Pedidos encontrados: ${orderData.length}`);
      } else {
        console.log('ℹ️ No hay pedidos asignados aún');
      }
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        'Error al cargar pedidos';
      setOrdersError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setOrders([]);
      setCurrentOrder(null);
    } finally {
      if (!silent) setLoadingOrders(false);
    }
  }, [driver, audioEnabled]);

  const updateLocation = useCallback(async (driverId, lat, lng) => {
    if (driverId == null) return;
    await apiClient.post(`/drivers/${driverId}/location/`, { lat, lng });
  }, []);

  const pollGeolocation = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ lat: latitude, lng: longitude });
        if (token && driver?.id) {
          updateLocation(driver.id, latitude, longitude)
            .catch((err) => console.error('Error updating location:', err));
        }
      },
      (error) => console.error('Error getting location:', error)
    );
  }, [token, driver, setLocation, updateLocation]);

  useEffect(() => {
    if (!(loggedIn && token && driver)) return;

    fetchOrders(driver.id, { silent: false });
    const locationInterval = setInterval(pollGeolocation, 10000);
    const ordersInterval = setInterval(() => fetchOrders(driver.id, { silent: true }), 3000);
    return () => {
      clearInterval(locationInterval);
      clearInterval(ordersInterval);
    };
  }, [loggedIn, token, driver, fetchOrders, pollGeolocation]);

  const handleDriverAction = useCallback(
    async (action, orderId) => {
      try {
        const response = await apiClient.post(`/orders/${orderId}/driver-action/`, { action });
        setCurrentOrder(response.data);
        await fetchOrders();
      } catch (error) {
        onErrorRef.current?.('Error al realizar la acción');
      }
    },
    [fetchOrders]
  );

  return {
    pedidos: orders,
    pedidoActivo: currentOrder,
    loadingOrders,
    ordersError,
    fetchOrders,
    handleDriverAction,
    updateLocation,
  };
}
